import { motion } from "framer-motion";
import { Flame } from "lucide-react";
import { BRAND } from "@/styles/brand";

const C = BRAND.colors;

const MILESTONES = [
  { days: 3, reward: "+25 Bonus-Punkte", emoji: "🔥" },
  { days: 7, reward: "+75 Bonus-Punkte", emoji: "💪" },
  { days: 14, reward: "Gratis Topping", emoji: "⭐" },
  { days: 30, reward: "Mystery Reward", emoji: "🎁" },
];

export default function StreakCounter({
  currentStreak = 5,
  longestStreak = 12,
  lastDays = [true, true, true, true, true, false, false],
  hasFreeze = true,
}) {
  const nextMilestone = MILESTONES.find(m => m.days > currentStreak);
  const daysToNext = nextMilestone ? nextMilestone.days - currentStreak : 0;
  const dayLabels = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-5"
      style={{
        borderRadius: 24,
        background: C.bgCard,
        boxShadow: BRAND.shadows.sm,
      }}
    >
      {/* Header with Flame */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${C.orange}20, ${C.orange}40)` }}
          >
            <Flame size={24} style={{ color: C.orange }} fill={C.orange} />
          </motion.div>
          <div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-3xl font-extrabold"
                style={{ color: C.text, fontFamily: BRAND.typography.heading }}>
                {currentStreak}
              </span>
              <span className="text-[13px] font-medium" style={{ color: C.textMuted }}>
                {currentStreak === 1 ? "Tag" : "Tage"}
              </span>
            </div>
            <p className="text-[12px]" style={{ color: C.textMuted }}>
              Rekord: {longestStreak} Tage
            </p>
          </div>
        </div>

        {/* Freeze Button */}
        {hasFreeze && (
          <button className="px-3 py-1.5 rounded-xl text-[11px] font-semibold"
            style={{ backgroundColor: C.bg, color: C.textMuted }}>
            ❄️ Freeze (1x)
          </button>
        )}
      </div>

      {/* Week Dots */}
      <div className="flex justify-between mb-4 px-1">
        {lastDays.map((visited, i) => (
          <div key={i} className="flex flex-col items-center gap-1.5">
            <span className="text-[10px] font-medium" style={{ color: C.textMuted }}>
              {dayLabels[i]}
            </span>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: i * 0.05 }}
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{
                backgroundColor: visited ? C.green : "transparent",
                border: visited ? "none" : `2px dashed ${C.border}`,
              }}
            >
              {visited ? (
                <span className="text-white text-xs font-bold">&#10003;</span>
              ) : (
                <span className="text-[11px]" style={{ color: C.border }}>-</span>
              )}
            </motion.div>
          </div>
        ))}
      </div>

      {/* Next Milestone */}
      {nextMilestone && (
        <div className="flex items-center gap-3 p-3 rounded-xl"
          style={{ backgroundColor: C.bg }}>
          <span className="text-xl">{nextMilestone.emoji}</span>
          <div className="flex-1">
            <p className="text-[12px] font-semibold" style={{ color: C.text }}>
              {daysToNext === 1 ? "Noch 1 Tag" : `Noch ${daysToNext} Tage`} bis zum {nextMilestone.days}-Tage-Bonus!
            </p>
            <p className="text-[11px]" style={{ color: C.green }}>
              {nextMilestone.reward}
            </p>
          </div>
        </div>
      )}

      {/* Milestone Markers */}
      <div className="flex justify-between mt-4 px-2">
        {MILESTONES.map((m) => {
          const reached = currentStreak >= m.days;
          return (
            <div key={m.days} className="flex flex-col items-center gap-1">
              <span className={`text-sm ${reached ? "" : "grayscale opacity-40"}`}>
                {m.emoji}
              </span>
              <span className="text-[10px] font-semibold"
                style={{ color: reached ? C.green : C.textMuted }}>
                {m.days}d
              </span>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}
